import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils';
import adminAnalyticsService from '../../../services/adminAnalyticsService';

const UserManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const userManagement = {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.isActive).length,
    suspendedUsers: users.filter(u => !u.isActive).length,
    newThisMonth: users.filter(u => {
      const created = new Date(u.joinDate);
      const now = new Date();
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    const result = await adminAnalyticsService.getUsers({ limit: 1000 });
    if (result.success) {
      setUsers(result.data.map(u => ({
        id: u.id,
        name: u.full_name,
        email: u.email,
        phone: u.phone,
        role: u.role,
        isActive: u.account_status === 'active',
        joinDate: u.created_at,
        lastLogin: u.last_sign_in_at || u.created_at,
        profileImage: u.profile_image
      })));
    }
    setLoading(false);
  };

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && user?.isActive) ||
                         (filterStatus === 'inactive' && !user?.isActive) ||
                         (filterStatus === 'premium' && user?.accountType === 'premium');
    return matchesSearch && matchesStatus;
  });

  const handleUserAction = (userId, action) => {
    setUsers(prev => prev?.map(user => {
      if (user?.id === userId) {
        switch (action) {
          case 'suspend':
            return { ...user, isActive: false };
          case 'activate':
            return { ...user, isActive: true };
          case 'upgrade':
            return { ...user, accountType: 'premium' };
          default:
            return user;
        }
      }
      return user;
    }));
  };

  const handleBulkAction = (action) => {
    if (selectedUsers?.length === 0) return;
    
    setUsers(prev => prev?.map(user => {
      if (selectedUsers?.includes(user?.id)) {
        switch (action) {
          case 'suspend':
            return { ...user, isActive: false };
          case 'activate':
            return { ...user, isActive: true };
          default:
            return user;
        }
      }
      return user;
    }));
    setSelectedUsers([]);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers?.length === filteredUsers?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers?.map(user => user?.id));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-text-primary">
          User Management
        </h2>
        <Button variant="primary" iconName="UserPlus">
          Add User
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="border-b border-border-light">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'users', label: 'All Users' },
              { id: 'analytics', label: 'Analytics' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 border-b-2 font-medium text-sm transition-smooth ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-primary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* User Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-600 text-sm font-medium">Total Users</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {userManagement?.totalUsers?.toLocaleString()}
                      </p>
                    </div>
                    <Icon name="Users" size={24} className="text-blue-600" />
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    +{userManagement?.newUsersThisMonth} this month
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-600 text-sm font-medium">Active Users</p>
                      <p className="text-2xl font-bold text-green-900">
                        {userManagement?.activeUsers?.toLocaleString()}
                      </p>
                    </div>
                    <Icon name="UserCheck" size={24} className="text-green-600" />
                  </div>
                  <p className="text-xs text-green-700 mt-2">
                    {((userManagement?.activeUsers / userManagement?.totalUsers) * 100)?.toFixed(1)}% active rate
                  </p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-600 text-sm font-medium">Premium Users</p>
                      <p className="text-2xl font-bold text-purple-900">
                        {userManagement?.premiumUsers?.toLocaleString()}
                      </p>
                    </div>
                    <Icon name="Crown" size={24} className="text-purple-600" />
                  </div>
                  <p className="text-xs text-purple-700 mt-2">
                    {((userManagement?.premiumUsers / userManagement?.totalUsers) * 100)?.toFixed(1)}% premium rate
                  </p>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-600 text-sm font-medium">Growth Rate</p>
                      <p className="text-2xl font-bold text-orange-900">
                        +{userManagement?.userGrowthRate}%
                      </p>
                    </div>
                    <Icon name="TrendingUp" size={24} className="text-orange-600" />
                  </div>
                  <p className="text-xs text-orange-700 mt-2">Monthly growth</p>
                </div>
              </div>

              {/* User Demographics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h3 className="font-semibold text-lg text-text-primary mb-4">Users by Location</h3>
                  <div className="space-y-3">
                    {userManagement?.usersByLocation?.map((location, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-text-primary">{location?.city}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full" 
                              style={{ width: `${(location?.count / Math.max(...userManagement?.usersByLocation?.map(l => l?.count))) * 100}%` }}
                            />
                          </div>
                          <span className="text-text-secondary text-sm">{location?.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h3 className="font-semibold text-lg text-text-primary mb-4">Users by Age</h3>
                  <div className="space-y-3">
                    {userManagement?.usersByAge?.map((age, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-text-primary">{age?.range}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-secondary h-2 rounded-full" 
                              style={{ width: `${(age?.count / Math.max(...userManagement?.usersByAge?.map(a => a?.count))) * 100}%` }}
                            />
                          </div>
                          <span className="text-text-secondary text-sm">{age?.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-4">
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                >
                  <option value="all">All Users</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="premium">Premium</option>
                </select>
              </div>

              {/* Bulk Actions */}
              {selectedUsers?.length > 0 && (
                <div className="flex items-center space-x-2 p-3 bg-primary-50 rounded-lg">
                  <span className="text-sm text-primary font-medium">
                    {selectedUsers?.length} user(s) selected
                  </span>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('activate')}>
                    Activate
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBulkAction('suspend')}>
                    Suspend
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedUsers([])}>
                    Clear
                  </Button>
                </div>
              )}

              {/* Users Table */}
              <div className="bg-white border border-border-light rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-surface-50">
                    <tr>
                      <th className="p-4 text-left">
                        <input
                          type="checkbox"
                          checked={selectedUsers?.length === filteredUsers?.length && filteredUsers?.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-border-light"
                        />
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">User</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Account Type</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Wedding Date</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Status</th>
                      <th className="p-4 text-left text-sm font-medium text-text-secondary">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-light">
                    {filteredUsers?.map((user) => (
                      <tr key={user?.id} className="hover:bg-surface-50">
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedUsers?.includes(user?.id)}
                            onChange={() => handleSelectUser(user?.id)}
                            className="rounded border-border-light"
                          />
                        </td>
                        <td className="p-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={user?.profileImage}
                              alt={user?.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div>
                              <p className="font-medium text-text-primary">{user?.name}</p>
                              <p className="text-sm text-text-secondary">{user?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user?.accountType === 'premium' ?'bg-purple-100 text-purple-600' :'bg-gray-100 text-gray-600'
                          }`}>
                            {user?.accountType}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-text-primary">
                          {formatDate(user?.weddingDate)}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user?.isActive 
                              ? 'bg-green-100 text-green-600' :'bg-red-100 text-red-600'
                          }`}>
                            {user?.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Eye"
                              onClick={() => console.log('View user:', user?.id)}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName={user?.isActive ? 'UserX' : 'UserCheck'}
                              onClick={() => handleUserAction(user?.id, user?.isActive ? 'suspend' : 'activate')}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="MoreHorizontal"
                              onClick={() => console.log('More actions for:', user?.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="font-heading font-semibold text-lg text-text-primary">
                User Analytics
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-4">Monthly User Growth</h4>
                  <div className="space-y-3">
                    {['Jan', 'Feb', 'Mar', 'Apr', 'May']?.map((month, index) => (
                      <div key={month} className="flex items-center justify-between">
                        <span className="text-text-primary">{month} 2024</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full" 
                              style={{ width: `${Math.random() * 100}%` }}
                            />
                          </div>
                          <span className="text-text-secondary text-sm">+{Math.floor(Math.random() * 200)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-border-light rounded-lg p-4">
                  <h4 className="font-medium text-text-primary mb-4">User Engagement</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Daily Active Users</span>
                      <span className="font-medium text-text-primary">8,924</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Average Session Time</span>
                      <span className="font-medium text-text-primary">12:34</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Bounce Rate</span>
                      <span className="font-medium text-text-primary">24.7%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Return Rate</span>
                      <span className="font-medium text-text-primary">68.2%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementPanel;